import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  addConsoleLine,
  addFeedNote,
  addSessionMessage,
  addSessionDiagnostic,
  endSession,
  resetGame,
  setSessionConnectionStatus,
  setSessionPuppetConfirmed,
  setSessionLifecycle,
  setSessionEntryError,
  resetSessionRoomRevision,
  setRoomStateResyncStatus,
} from '@/store/gameSlice';
import { setAccount } from '@/store/authSlice';
import { parseGameMessage } from './parseGameMessage';
import { WS_MESSAGE_TYPE, EVENNIA_CONTROL_TYPES, SERVER_QUIT_CLOSE_REASONS } from './types';
import { classifyText } from '@/game/feedKinds';
import { emitActionResult } from './actionResultBus';
import { emitHazardPrompt } from './hazardPromptBus';
import { recordUnknownFrame } from './unknownFrames';

import type {
  ActionResultPayload,
  CommandErrorPayload,
  HazardPromptPayload,
  IncomingMessage,
  InteractionWsPayload,
  KudosReceivedPayload,
  MailArrivedPayload,
  OutgoingMessage,
  RoomStatePayload,
  ScenePayload,
  SocketMessageType,
} from './types';
import type { CommandSpec } from '@/game/types';
import { handleRoomStatePayload } from './handleRoomStatePayload';
import { handleScenePayload } from './handleScenePayload';
import { handleCommandPayload } from './handleCommandPayload';
import { handleInteractionPayload } from './handleInteractionPayload';
import { handleRoulettePayload } from './handleRoulettePayload';
import type { RoulettePayload } from '@/components/roulette/types';
import { handleBattleStatePayload } from './handleBattleStatePayload';
import {
  handleCombatEncounterStatePayload,
  type CombatEncounterStatePayload,
} from './handleCombatEncounterStatePayload';
import type { BattleStatePayload } from '@/battles/types';
import { handleKudosReceivedPayload } from './handleKudosReceivedPayload';
import { handleMailArrivedPayload } from './handleMailArrivedPayload';
import { handleGMPromptPayload } from './handleGMPromptPayload';
import {
  handleInteractionChangedPayload,
  type InteractionChangedPayload,
} from './handleInteractionChangedPayload';
import { sceneFeedAllKey, sceneFeedSceneKey } from '@/scenes/sceneFeedCache';

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NavigateFunction } from 'react-router-dom';
import type { MyRosterEntry } from '@/roster/types';
import type { AppDispatch } from '@/store/store';
import { getWebSocketUrl } from '@/config';
import { toast } from 'sonner';
import { fetchAccount } from '@/evennia_replacements/api';
import { queryClient } from '@/queryClient';
import { markTargetMenusStale } from '@/game/target-menu/targetMenuApi';
import { reconcileStoredDrafts } from '@/game/useDraftStore';
import { fetchPoseSubmission } from '@/scenes/queries';
import { connectionDiagnostics } from '@/diagnostics/connectionDiagnostics';

const sockets: Record<string, WebSocket> = {};
// Names with a connect() in flight (pre-socket-creation await window) — the
// synchronous guard that prevents two near-simultaneous connects from each
// opening a socket and leaking the first (2026-07 audit).
const connecting = new Set<string>();
// Per-character reconnect bookkeeping (2026-07 audit): an abnormal close used
// to just mark the session disconnected and stop — a network blip silently
// froze the feed until the user manually re-clicked their character tab.
const reconnectAttempts: Record<string, number> = {};
const reconnectTimers: Record<string, ReturnType<typeof setTimeout>> = {};
// A successful websocket is not entry-ready until a room snapshot arrives.
// Give the normal puppet/entry path a bounded window, then make one explicit
// same-connection resync request before exposing recovery UI.
const entryRecoveryTimers: Record<string, ReturnType<typeof setTimeout>> = {};
const ENTRY_RECOVERY_WAIT_MS = 5000;
const MAX_RECONNECT_ATTEMPTS = 6;
// CloseEvent.code does not identify who closed a socket. Keep that intent on
// the socket itself so a deliberate close cannot be mistaken for a peer loss.
const localCloseIntent = new WeakSet<WebSocket>();
// The close code a wake resume sends for the socket it replaces. It must be a
// code WebSocket.close() accepts, 1000 or 3000-4999; see resume() (#4026).
export const PAGE_RESUME_CLOSE_CODE = 4000;

type ConnectionReadiness = {
  socket: WebSocket;
  generation: number;
  puppetConfirmed: boolean;
  roomStateAccepted: boolean;
  reconciliationComplete: boolean;
  ready: boolean;
};
const connectionReadiness: Record<string, ConnectionReadiness> = {};
// Per-character monotonic connection generation (#3760): incremented on every
// connection attempt, including automatic reconnects. Each connection's message
// handler closes over the generation it was created with, so a message that
// arrives after that connection has been superseded (a newer generation now
// current) can be dropped before it reaches any state update - reconnects are
// frequent in production (#3745), and a stale message flipping readiness or
// clearing an in-flight draft is exactly what this guards against.
const connectionGenerations: Record<string, number> = {};

type PendingResync = {
  character: MyRosterEntry['name'];
  generation: number;
  requestId: string;
  timer: ReturnType<typeof setTimeout>;
  snapshotAccepted: boolean;
  snapshotRevision?: { epoch: string; sequence: number };
};
const pendingResync = new Map<string, PendingResync>();

function finishResync(
  pending: PendingResync,
  dispatch: AppDispatch,
  status: 'success' | 'partial' | 'failure',
  error?: string
): void {
  if (pendingResync.get(pending.requestId) !== pending) return;
  clearTimeout(pending.timer);
  pendingResync.delete(pending.requestId);
  dispatch(
    setRoomStateResyncStatus({ character: pending.character, status, ...(error ? { error } : {}) })
  );
}

function markResyncSnapshot(
  character: MyRosterEntry['name'],
  generation: number,
  requestId: string,
  kwargs: Record<string, unknown>,
  dispatch: AppDispatch
): void {
  const pending = pendingResync.get(requestId);
  if (!pending || pending.character !== character || pending.generation !== generation) return;
  pending.snapshotAccepted = true;
  if (typeof kwargs.state_epoch === 'string' && Number.isInteger(kwargs.state_sequence)) {
    pending.snapshotRevision = {
      epoch: kwargs.state_epoch,
      sequence: kwargs.state_sequence as number,
    };
  }
  // Keep pending until the acknowledgement arrives; the timeout/close path
  // reports partial success when only the snapshot made it through.
  dispatch(setRoomStateResyncStatus({ character, status: 'pending' }));
}

function finishGenerationResyncs(
  character: MyRosterEntry['name'],
  generation: number,
  dispatch: AppDispatch
): void {
  for (const pending of pendingResync.values()) {
    if (pending.character !== character || pending.generation !== generation) continue;
    finishResync(
      pending,
      dispatch,
      pending.snapshotAccepted ? 'partial' : 'failure',
      pending.snapshotAccepted
        ? 'Room state refreshed, but confirmation was lost.'
        : 'Room state refresh failed. Retry.'
    );
  }
}

function nextGeneration(character: string): number {
  const next = (connectionGenerations[character] ?? 0) + 1;
  connectionGenerations[character] = next;
  return next;
}

/**
 * Exported ONLY for use in beforeEach in test files. Do not call in production
 * code. Clears every module-level record this file keeps (`sockets`,
 * `connecting`, `reconnectAttempts`, `connectionGenerations`) and cancels any
 * pending reconnect timers before clearing `reconnectTimers` itself — a
 * leftover `setTimeout` from a prior test's abnormal-close path would
 * otherwise fire mid-way through a later, unrelated test.
 */
export function __resetGameSocketModuleStateForTests(): void {
  Object.keys(sockets).forEach((character) => delete sockets[character]);
  connecting.clear();
  Object.keys(reconnectAttempts).forEach((character) => delete reconnectAttempts[character]);
  Object.values(reconnectTimers).forEach((timer) => clearTimeout(timer));
  Object.keys(reconnectTimers).forEach((character) => delete reconnectTimers[character]);
  Object.values(entryRecoveryTimers).forEach((timer) => clearTimeout(timer));
  Object.keys(entryRecoveryTimers).forEach((character) => delete entryRecoveryTimers[character]);
  Object.keys(connectionReadiness).forEach((character) => delete connectionReadiness[character]);
  Object.keys(connectionGenerations).forEach(
    (character) => delete connectionGenerations[character]
  );
  pendingResync.forEach((pending) => clearTimeout(pending.timer));
  pendingResync.clear();
}

/** Swallow reconnect failures so a transient socket error doesn't reject the timer. */
const swallowReconnectError = (): void => {};

function cancelReconnectTimer(character: string): void {
  const timer = reconnectTimers[character];
  if (timer !== undefined) {
    clearTimeout(timer);
    delete reconnectTimers[character];
  }
}

function clearEntryRecovery(character: string): void {
  const timer = entryRecoveryTimers[character];
  if (timer !== undefined) {
    clearTimeout(timer);
    delete entryRecoveryTimers[character];
  }
}

/** Cancel pending work and reset the retry budget after an explicit action. */
function clearReconnect(character: string): void {
  cancelReconnectTimer(character);
  reconnectAttempts[character] = 0;
}

function markConnectionReady(
  character: MyRosterEntry['name'],
  generation: number,
  socket: WebSocket,
  milestone: 'puppetConfirmed' | 'roomStateAccepted' | 'reconciliationComplete',
  dispatch: AppDispatch
): void {
  const state = connectionReadiness[character];
  if (!state || state.socket !== socket || state.generation !== generation) return;
  state[milestone] = true;
  if (
    state.ready ||
    !state.puppetConfirmed ||
    !state.roomStateAccepted ||
    !state.reconciliationComplete
  ) {
    return;
  }
  state.ready = true;
  // A retry is successful only after the application is usable, not when the
  // transport merely emits `open`.
  reconnectAttempts[character] = 0;
  dispatch(setSessionConnectionStatus({ character, status: true }));
  queryClient.invalidateQueries({ queryKey: sceneFeedAllKey() }).catch(() => {});
  // #4321: the roster payload carries the server's session count, which every
  // tab's chip and Hall card read; this tab just added a session to it.
  refetchPresence();
}

/**
 * Refetch the roster list that carries `session_count`/`room_name` (#4321),
 * now and once more a moment later: the server unpuppets on the Portal's
 * disconnect, a beat after the browser's close event, so the first refetch
 * after a close can still see the session. The chip reads this tab's own
 * socket first, so the gap is only ever visible to another tab.
 */
function refetchPresence(): void {
  queryClient.invalidateQueries({ queryKey: ['my-roster-entries'] }).catch(() => {});
  setTimeout(() => {
    queryClient.invalidateQueries({ queryKey: ['my-roster-entries'] }).catch(() => {});
  }, PRESENCE_SETTLE_MS);
}

/** How long after a socket closes before the server has surely unpuppeted. */
const PRESENCE_SETTLE_MS = 1500;

function markEntryFailure(character: MyRosterEntry['name'], dispatch: AppDispatch): void {
  // A refusal is actionable, not a transient transport loss. Prevent a later
  // close callback from spending the reconnect budget on the same refusal.
  cancelReconnectTimer(character);
  reconnectAttempts[character] = MAX_RECONNECT_ATTEMPTS;
  dispatch(setSessionLifecycle({ character, lifecycleState: 'entry-error' }));
}

/** Narrows a parsed frame to the `[type, args, kwargs?]` wire shape. */
function isIncomingMessage(value: unknown): value is IncomingMessage {
  return Array.isArray(value) && value.length >= 2;
}

interface IncomingMessageContext {
  character: MyRosterEntry['name'];
  args: unknown[];
  kwargs: Record<string, unknown> | undefined;
  dispatch: AppDispatch;
  navigate: NavigateFunction;
  onPuppetConfirmed?: () => void;
  onRoomStateAccepted?: () => void;
  onEntryFailure?: () => void;
}

type IncomingMessageHandler = (ctx: IncomingMessageContext) => boolean | void;

// One case per control message type the server can push; anything not matched
// here falls through to parseGameMessage as a regular game message. A switch on
// the literal type constants keeps this dispatch's cognitive complexity in check
// the way the old if-chain did not, while keeping the chosen handler a matter of
// explicit control flow. An object/Map lookup keyed by the wire-supplied type
// reads the same but makes the call target a value derived from user input,
// which is exactly what CodeQL js/unvalidated-dynamic-method-call flags.
function handlerFor(msgType: SocketMessageType): IncomingMessageHandler | undefined {
  switch (msgType) {
    case WS_MESSAGE_TYPE.STATE_RESYNC:
      return ({ character, kwargs, dispatch }) => {
        if (
          typeof kwargs?.client_request_id !== 'string' ||
          typeof kwargs.state_epoch !== 'string' ||
          !Number.isInteger(kwargs.state_sequence) ||
          typeof kwargs.room_id !== 'number' ||
          !Number.isInteger(kwargs.room_id) ||
          (kwargs.scene_id !== null &&
            (typeof kwargs.scene_id !== 'number' || !Number.isInteger(kwargs.scene_id)))
        ) {
          return false;
        }
        const pending = pendingResync.get(kwargs.client_request_id);
        if (
          !pending ||
          pending.character !== character ||
          !pending.snapshotAccepted ||
          !pending.snapshotRevision ||
          pending.snapshotRevision.epoch !== kwargs.state_epoch ||
          pending.snapshotRevision.sequence !== kwargs.state_sequence
        )
          return false;
        const invalidations: Promise<unknown>[] = [];
        if (kwargs.scene_id !== null) {
          invalidations.push(
            queryClient.invalidateQueries({
              queryKey: sceneFeedSceneKey(kwargs.scene_id),
            })
          );
          invalidations.push(
            queryClient.invalidateQueries({
              queryKey: ['scene-places', String(kwargs.room_id), character],
              exact: true,
            })
          );
        }
        Promise.all(invalidations).then(
          () => finishResync(pending, dispatch, 'success'),
          () =>
            finishResync(
              pending,
              dispatch,
              'partial',
              'Room state refreshed, but scene data could not be refreshed.'
            )
        );
        return true;
      };

    case WS_MESSAGE_TYPE.STATE_RESYNC_ERROR:
      return ({ character, kwargs, dispatch }) => {
        if (typeof kwargs?.client_request_id !== 'string' || typeof kwargs.code !== 'string') {
          return false;
        }
        const pending = pendingResync.get(kwargs.client_request_id);
        if (!pending || pending.character !== character) return false;
        finishResync(pending, dispatch, 'failure', 'Room state refresh failed. Retry.');
        dispatch(setSessionLifecycle({ character, lifecycleState: 'entry-error' }));
        return true;
      };

    case WS_MESSAGE_TYPE.ROOM_STATE:
      return ({ character, kwargs, dispatch, onRoomStateAccepted }) => {
        const accepted = handleRoomStatePayload(
          character,
          kwargs as unknown as RoomStatePayload,
          dispatch
        );
        if (accepted) onRoomStateAccepted?.();
        return accepted;
      };

    case WS_MESSAGE_TYPE.SCENE:
      return ({ character, kwargs, dispatch }) =>
        handleScenePayload(character, kwargs as unknown as ScenePayload, dispatch);

    case WS_MESSAGE_TYPE.COMMAND_ERROR:
      return ({ character, kwargs, dispatch, onEntryFailure }) => {
        const { error, command } = (kwargs as unknown as CommandErrorPayload) ?? {};
        if (typeof error === 'string' && command === WS_MESSAGE_TYPE.PUPPET) {
          clearEntryRecovery(character);
          // Keep the server's refusal visible in the entry placeholder as well
          // as the toast. The lifecycle transition below is intentionally
          // limited to the puppet handshake, not ordinary command failures.
          dispatch(setSessionEntryError({ character, error }));
        }
        toast.error(error, { description: command });
        if (command === 'puppet') onEntryFailure?.();
      };

    case WS_MESSAGE_TYPE.KUDOS_RECEIVED:
      return ({ kwargs }) =>
        handleKudosReceivedPayload(kwargs as unknown as KudosReceivedPayload | undefined);

    // Defensive: kwargs may be undefined if the server sends a malformed frame.
    // emitActionResult is a side-effecting bus call only - every listener is
    // responsible for its own toast/UX.
    case WS_MESSAGE_TYPE.ACTION_RESULT:
      return ({ kwargs }) =>
        emitActionResult(
          (kwargs as unknown as ActionResultPayload) ?? {
            success: false,
            message: null,
            data: null,
          }
        );

    case WS_MESSAGE_TYPE.ROULETTE_RESULT:
      return ({ kwargs, dispatch }) =>
        handleRoulettePayload(kwargs as unknown as RoulettePayload, dispatch);

    case WS_MESSAGE_TYPE.BATTLE_STATE:
      return ({ kwargs }) => handleBattleStatePayload(kwargs as unknown as BattleStatePayload);

    case WS_MESSAGE_TYPE.COMBAT_ENCOUNTER_STATE:
      return ({ kwargs }) =>
        handleCombatEncounterStatePayload(kwargs as unknown as CombatEncounterStatePayload);

    case WS_MESSAGE_TYPE.MAIL_ARRIVED:
      return ({ kwargs }) => handleMailArrivedPayload(kwargs as unknown as MailArrivedPayload);

    case WS_MESSAGE_TYPE.HAZARD_PROMPT:
      return ({ kwargs }) => emitHazardPrompt(kwargs as unknown as HazardPromptPayload);

    case WS_MESSAGE_TYPE.INTERACTION:
      return ({ character, kwargs, dispatch, navigate }) =>
        handleInteractionPayload(
          character,
          kwargs as unknown as InteractionWsPayload,
          dispatch,
          navigate
        );

    case WS_MESSAGE_TYPE.COMMANDS:
      return ({ character, args, kwargs }) =>
        handleCommandPayload(character, args as CommandSpec[], kwargs);

    // Broadcast to every account session on each successful puppet (#3933).
    // The frame carries a `session_id`, but a socket does not know its own, so
    // the match is on `character_name` alone. What that proves is narrower than
    // "my request landed": SOME session of this account now puppets this
    // socket's character. In the common single-tab case that is this socket's
    // own open-time request; with two tabs on the same character, the sibling's
    // puppet confirms this one too. A per-session confirmation needs the server
    // to echo a request id back on the frame, which #3934's connection-recorder
    // work adds. A puppet_changed for another character (a sibling tab on a
    // different character) is never this socket's confirmation and is ignored.
    case WS_MESSAGE_TYPE.PUPPET_CHANGED:
      return ({ character, kwargs, dispatch, onPuppetConfirmed }) => {
        if (kwargs?.character_name === character) {
          dispatch(setSessionPuppetConfirmed({ character }));
          onPuppetConfirmed?.();
        }
      };

    // The death condolence line (#3933): toast it, the way command_error's
    // refusal is already toasted.
    case WS_MESSAGE_TYPE.CHARACTER_DIED:
      return ({ kwargs }) => {
        if (typeof kwargs?.body === 'string') toast(kwargs.body);
      };

    // The REST view owns the estate settlement surface; nothing to do here (#3933).
    case WS_MESSAGE_TYPE.ESTATE_SETTLEMENT_OPENED:
      return () => undefined;

    // Evennia's own out-of-band echo; no Arx meaning (#3933).
    case WS_MESSAGE_TYPE.OOB:
      return () => undefined;

    // Evennia's client-settings frame; no Arx meaning (#3933).
    case WS_MESSAGE_TYPE.WEBCLIENT_OPTIONS:
      return () => undefined;

    // A new GM prompt landed in this GM's queue (#4101): refetch every open
    // queue for this GM (`GMPromptQueue`'s `useGMPrompts`).
    case WS_MESSAGE_TYPE.GM_PROMPT:
      return () => handleGMPromptPayload();

    // A pose's endorsements, tags or reactions changed (#4508): refetch that row.
    case WS_MESSAGE_TYPE.INTERACTION_CHANGED:
      return ({ kwargs }) =>
        handleInteractionChangedPayload(kwargs as unknown as InteractionChangedPayload);

    default:
      return undefined;
  }
}
/** Applies lifecycle transitions associated with handled protocol frames. */
function updateLifecycle(
  character: MyRosterEntry['name'],
  msgType: SocketMessageType,
  kwargs: Record<string, unknown> | undefined,
  accepted: boolean | void,
  dispatch: AppDispatch
): void {
  if (msgType === WS_MESSAGE_TYPE.ROOM_STATE && accepted !== false) {
    const roomPayload = kwargs as { scene?: unknown } | undefined;
    dispatch(
      setSessionLifecycle({
        character,
        lifecycleState: roomPayload?.scene ? 'ready-scene' : 'ready-no-scene',
      })
    );
    return;
  }
  if (msgType === WS_MESSAGE_TYPE.COMMAND_ERROR && kwargs?.command === WS_MESSAGE_TYPE.PUPPET) {
    dispatch(setSessionLifecycle({ character, lifecycleState: 'entry-error' }));
    return;
  }
  if (msgType !== WS_MESSAGE_TYPE.SCENE) return;
  const scenePayload = kwargs as { action?: unknown } | undefined;
  if (scenePayload?.action === 'end') {
    dispatch(setSessionLifecycle({ character, lifecycleState: 'aftermath' }));
  }
}

const LEGACY_TEXT_TYPES = new Set<SocketMessageType>([
  WS_MESSAGE_TYPE.TEXT,
  WS_MESSAGE_TYPE.LOGGED_IN,
  WS_MESSAGE_TYPE.VN_MESSAGE,
  WS_MESSAGE_TYPE.MESSAGE_REACTION,
]);

/**
 * Renders legacy text-like frames. A `text` frame becomes a feed note (#3856),
 * its kind read from `kwargs.type` (the dict of Evennia's tuple form, which is
 * how commands, movement announcements and the narrative service type their
 * lines); the readers render notes at their timestamp among the interactions.
 * The other legacy frames (login, VN, reactions) keep the message lane.
 */
function dispatchLegacyText(
  character: MyRosterEntry['name'],
  parsed: IncomingMessage,
  msgType: SocketMessageType,
  kwargs: Record<string, unknown> | undefined,
  dispatch: AppDispatch
): boolean {
  if (!LEGACY_TEXT_TYPES.has(msgType)) return false;
  // A milestone the server marks with the puppet handshake, nothing to
  // render (#3933) - `logged_in` used to land in the message lane below.
  if (msgType === WS_MESSAGE_TYPE.LOGGED_IN) return true;
  const message = parseGameMessage(parsed);
  if (msgType === WS_MESSAGE_TYPE.TEXT) {
    // A frame the server tagged for the staff console (#3857) is the answer
    // to a Commands-mode line; it belongs to the console, never the column.
    if (kwargs?.console === true) {
      dispatch(addConsoleLine({ character, content: message.content }));
      return true;
    }
    // Three tags a `text` frame carries for a compatibility line that has
    // its own render elsewhere, so no note is added for it (#3933):
    // `interaction_echo` (the structured Interaction pushed alongside it is
    // the render), `type: 'lifecycle'` (a login milestone, not story), and
    // `on_entry` (the room panel already shows the entry look).
    if (
      kwargs?.interaction_echo === true ||
      kwargs?.type === 'lifecycle' ||
      kwargs?.on_entry === true
    ) {
      return true;
    }
    dispatch(addFeedNote({ character, note: legacyFeedNote(message, kwargs) }));
  } else {
    dispatch(addSessionMessage({ character, message }));
  }
  return true;
}

/** Routes one parsed incoming websocket frame to its handler, or renders it as a plain game message. */
function dispatchKnownMessage(
  character: MyRosterEntry['name'],
  msgType: SocketMessageType,
  args: IncomingMessage[1],
  kwargs: IncomingMessage[2],
  handler: IncomingMessageHandler,
  dispatch: AppDispatch,
  navigate: NavigateFunction,
  generation: number,
  onPuppetConfirmed?: () => void,
  onRoomStateAccepted?: () => void,
  onEntryFailure?: () => void
): void {
  const accepted = handler({
    character,
    args,
    kwargs,
    dispatch,
    navigate,
    onPuppetConfirmed,
    onRoomStateAccepted,
    onEntryFailure,
  });
  updateLifecycle(character, msgType, kwargs, accepted, dispatch);
  if (msgType === WS_MESSAGE_TYPE.ROOM_STATE && accepted !== false) {
    clearEntryRecovery(character);
    if (typeof kwargs?.resync_request_id === 'string') {
      markResyncSnapshot(
        character,
        generation,
        kwargs.resync_request_id,
        kwargs as Record<string, unknown>,
        dispatch
      );
    }
  }
  if (accepted === false) {
    dispatch(
      addSessionDiagnostic({
        character,
        message: 'A connection message was malformed or out of sequence. Try again.',
      })
    );
  }
}

function dispatchIncomingMessage(
  character: MyRosterEntry['name'],
  parsed: IncomingMessage,
  dispatch: AppDispatch,
  navigate: NavigateFunction,
  generation: number,
  onPuppetConfirmed?: () => void,
  onRoomStateAccepted?: () => void,
  onEntryFailure?: () => void
): void {
  const [msgType, args, kwargs] = parsed;
  connectionDiagnostics.frame(msgType, 'parsed', generation);
  if (msgType === WS_MESSAGE_TYPE.PUPPET_CHANGED)
    connectionDiagnostics.readiness('puppet_confirmed', generation);
  if (msgType === WS_MESSAGE_TYPE.ROOM_STATE)
    connectionDiagnostics.readiness('room_state_accepted', generation);
  const handler = handlerFor(msgType);
  if (handler) {
    dispatchKnownMessage(
      character,
      msgType,
      args,
      kwargs,
      handler,
      dispatch,
      navigate,
      generation,
      onPuppetConfirmed,
      onRoomStateAccepted,
      onEntryFailure
    );
    return;
  }
  if (dispatchLegacyText(character, parsed, msgType, kwargs, dispatch)) return;
  // An Evennia protocol frame (channel, ping, ...) has no Arx meaning and is
  // never story content; anything else is a genuine gap in this client's
  // coverage, recorded for a developer rather than alarmed at a player (#3933).
  if (EVENNIA_CONTROL_TYPES.has(msgType)) return;
  recordUnknownFrame(msgType, generation);
}

export function useGameSocket() {
  const dispatch = useAppDispatch();
  const account = useAppSelector((state) => state.auth.account);
  const navigate = useNavigate();

  const disconnectAll = useCallback(() => {
    // Explicit disconnect: cancel any pending reconnects first so a closing
    // socket doesn't immediately resurrect itself. The close code is not a
    // reliable intent signal, so mark each socket before closing it.
    Object.keys(reconnectTimers).forEach(clearReconnect);
    Object.entries(sockets).forEach(([character, socket]) => {
      const generation = connectionGenerations[character] ?? 0;
      connectionDiagnostics.localClose(character, generation, 'disconnect_all');
      localCloseIntent.add(socket);
      socket.close();
    });
  }, []);

  /**
   * Leave the world as one character (#3818): close that character's socket
   * on purpose (a normal close, so the close handler never schedules a
   * reconnect) and forget its session. The server unpuppets when the last
   * session on the character goes, so the character leaves the grid rather
   * than standing there unpiloted. The durable selection is untouched — the
   * player is still playing this character, offscreen; `/game` re-enters.
   */
  const disconnect = useCallback(
    (character: MyRosterEntry['name']) => {
      clearReconnect(character);
      const socket = sockets[character];
      if (socket) {
        connectionDiagnostics.localClose(
          character,
          connectionGenerations[character] ?? 0,
          'disconnect'
        );
        localCloseIntent.add(socket);
        socket.close();
      }
      dispatch(endSession(character));
    },
    [dispatch]
  );

  const connect = useCallback(
    async (character: MyRosterEntry['name'], automaticReconnect = false) => {
      if (sockets[character] || connecting.has(character)) return;
      cancelReconnectTimer(character);
      // A manual entry/retry starts a fresh budget. Automatic retries carry the
      // budget until the application reaches readiness.
      if (!automaticReconnect) reconnectAttempts[character] = 0;
      connecting.add(character);
      dispatch(setSessionLifecycle({ character, lifecycleState: 'entering' }));
      const generation = nextGeneration(character);
      connectionDiagnostics.record(
        'socket_attempt',
        { attempt: (reconnectAttempts[character] ?? 0) + 1, outcome: 'started' },
        undefined,
        generation
      );

      let currentAccount = account;
      if (!currentAccount) {
        try {
          currentAccount = await fetchAccount();
          if (currentAccount) {
            dispatch(setAccount(currentAccount));
          } else {
            connectionDiagnostics.readiness('account_fetch_failed', generation);
            connecting.delete(character);
            dispatch(setSessionLifecycle({ character, lifecycleState: 'entry-error' }));
            navigate('/login');
            return;
          }
        } catch {
          connectionDiagnostics.readiness('account_fetch_failed', generation);
          connecting.delete(character);
          dispatch(setSessionLifecycle({ character, lifecycleState: 'entry-error' }));
          navigate('/login');
          return;
        }
      }

      // Clean websocket URL - middleware will inject session auth from cookies
      const url = getWebSocketUrl(window.location);
      let socket: WebSocket;
      try {
        socket = new WebSocket(url);
      } catch {
        connectionDiagnostics.record(
          'socket_attempt',
          { attempt: (reconnectAttempts[character] ?? 0) + 1, outcome: 'constructor_failed' },
          undefined,
          generation
        );
        connecting.delete(character);
        dispatch(setSessionLifecycle({ character, lifecycleState: 'entry-error' }));
        return;
      }
      sockets[character] = socket;
      connectionDiagnostics.socketStarted(character, generation);
      connectionReadiness[character] = {
        socket,
        generation,
        puppetConfirmed: false,
        roomStateAccepted: false,
        reconciliationComplete: false,
        ready: false,
      };
      connecting.delete(character);

      socket.addEventListener('open', () => {
        connectionDiagnostics.record('socket_open', {}, undefined, generation);
        connectionDiagnostics.readiness('socket_open', generation);
        // Opening the transport cancels a stale timer, but does not reset the
        // retry budget until all application readiness milestones complete.
        cancelReconnectTimer(character);
        // A new socket generation starts a fresh ordering baseline. Stale
        // callbacks from the previous generation are rejected by socket and
        // generation guards before they can reach Redux.
        dispatch(resetSessionRoomRevision({ character }));
        dispatch(setSessionLifecycle({ character, lifecycleState: 'entering' }));

        // Step 1: reauthorize. Puppet immediately — everything downstream
        // (room state, the ability to send) depends on this landing first.
        // Since #3812 the server already puppets the durable selection at
        // login, so for the selected character this is an idempotent no-op;
        // for a socket opening on another character, this is what puppets it
        // here. The reply is `puppet_changed`, not text (#3933) - see
        // `handlerFor`'s PUPPET_CHANGED case, which confirms the handshake.
        // `setSessionConnectionStatus` (connected=true) is intentionally NOT
        // dispatched here — see Step 3 below, which fires it only after
        // reconciliation, not on the raw socket 'open' event.
        const puppet: OutgoingMessage = [WS_MESSAGE_TYPE.PUPPET, [], { character }];
        connectionDiagnostics.readiness('puppet_requested', generation);
        socket.send(JSON.stringify(puppet));
        clearEntryRecovery(character);
        entryRecoveryTimers[character] = setTimeout(() => {
          delete entryRecoveryTimers[character];
          if (generation !== connectionGenerations[character]) return;
          requestRoomState(character);
        }, ENTRY_RECOVERY_WAIT_MS);

        // Step 2: reconcile (#3760 Task 12). A reconnect means any send
        // dispatched on the now-superseded connection may never have gotten
        // its ACTION_RESULT - the connection that would have delivered it is
        // gone, and a fresh connection's message handler runs under a new
        // generation that will never receive a frame addressed to the old
        // one (see the message listener's generation-discard check below).
        // So before this connection is allowed to declare the session ready,
        // resolve every stranded pending/unknown draft's fate against the
        // Task 6 lookup endpoint - the only reconciliation channel reachable
        // from here (this handler is module-scope, outside React, so it has
        // no live `beginSend()` to resend through; see `reconcileStoredDrafts`'s
        // doc comment in useDraftStore.ts for the full reasoning).
        reconcileStoredDrafts(fetchPoseSubmission)
          .catch(() => {
            // A rejected lookup already left its own stored draft untouched
            // (see reconcileStoredDrafts) - nothing further to do here.
          })
          .finally(() => {
            // Reconciliation is only one of the readiness milestones. The
            // retry budget remains active until the puppet confirmation and a
            // valid room snapshot have also arrived.
            markConnectionReady(character, generation, socket, 'reconciliationComplete', dispatch);
          });
      });

      socket.addEventListener('error', () => {
        connectionDiagnostics.record('socket_error', {}, undefined, generation);
      });

      socket.addEventListener('close', (event) => {
        connectionDiagnostics.socketClosed(character, generation, event.code, event.wasClean);
        // A reconnect can replace this socket before its close event arrives.
        // Never let stale frames or stale closes mutate the current session.
        if (sockets[character] !== socket) return;
        clearEntryRecovery(character);
        const wasLocalClose = localCloseIntent.has(socket);
        // A typed `quit` is the player leaving too, but the server is the one
        // that closes the socket for it, so nothing marked it locally. The
        // close reason is how the server says so; without reading it the
        // backoff below reconnected a second later and the quit did nothing.
        const wasServerQuit = !wasLocalClose && SERVER_QUIT_CLOSE_REASONS.has(event.reason);
        dispatch(setSessionConnectionStatus({ character, status: false }));
        finishGenerationResyncs(character, generation, dispatch);
        delete sockets[character];
        delete connectionReadiness[character];
        refetchPresence();
        if (wasLocalClose || wasServerQuit) {
          clearReconnect(character);
          // The rest of "Leave the world" (#3818), which `disconnect` and its
          // menu item do themselves for a local close.
          if (wasServerQuit) dispatch(endSession(character));
          // Only reset game state if this was the last active connection.
          const remainingConnections = Object.keys(sockets).length;
          if (remainingConnections === 0) {
            dispatch(resetGame());
          }
          if (wasServerQuit) {
            queryClient.invalidateQueries({ queryKey: ['account'] }).catch(() => {});
            navigate('/hall');
          }
          return;
        }
        // The peer/network owns the close outcome. Wire codes (including 1000,
        // 1001, and 1006) do not prove local intent, so all remote closes use
        // the same capped exponential backoff.
        const attempt = (reconnectAttempts[character] ?? 0) + 1;
        reconnectAttempts[character] = attempt;
        dispatch(
          setSessionLifecycle({
            character,
            lifecycleState: attempt > MAX_RECONNECT_ATTEMPTS ? 'entry-error' : 'reconnecting',
          })
        );
        if (attempt > MAX_RECONNECT_ATTEMPTS) {
          connectionDiagnostics.reconnect('reconnect_exhausted', { attempt }, generation);
          return;
        }
        const delay = Math.min(1000 * 2 ** (attempt - 1), 30_000);
        connectionDiagnostics.reconnect(
          'reconnect_scheduled',
          { attempt, delayMs: delay },
          generation
        );
        reconnectTimers[character] = setTimeout(() => {
          delete reconnectTimers[character];
          connectionDiagnostics.reconnect('reconnect_fired', { attempt }, generation);
          connect(character, true).catch(swallowReconnectError);
        }, delay);
      });

      socket.addEventListener('message', (event) => {
        // Discard before any state update: this connection may have already
        // been superseded by a newer one (e.g. a reconnect fired) by the time
        // this frame arrives. The generation check catches this earliest (it
        // advances the instant a new connect() starts, before that attempt's
        // socket even exists); the socket-identity check is a second,
        // independent guard against a stale frame from an old socket object.
        if (generation !== connectionGenerations[character]) return;
        if (sockets[character] !== socket) return;

        let parsed: unknown;

        try {
          parsed = JSON.parse(event.data);
        } catch {
          connectionDiagnostics.frame('unknown', 'malformed_json', generation);
          // Bad JSON is a diagnostic, not story content.
          dispatch(
            addSessionDiagnostic({
              character,
              message: 'A connection message was invalid. Try again.',
            })
          );
          return;
        }

        if (!isIncomingMessage(parsed)) {
          connectionDiagnostics.frame('unknown', 'unexpected_shape', generation);
          // Unexpected structure is a diagnostic, not story content.
          dispatch(
            addSessionDiagnostic({
              character,
              message: 'A connection message had an unexpected format.',
            })
          );
          return;
        }

        if (parsed[0] === WS_MESSAGE_TYPE.ACTION_RESULT) {
          const actorId = account?.available_characters.find(
            (entry) => entry.name === character
          )?.id;
          if (account !== null && actorId !== undefined) {
            void markTargetMenusStale(queryClient, `account-${account.id}`, actorId);
          }
        }

        dispatchIncomingMessage(
          character,
          parsed,
          dispatch,
          navigate,
          generation,
          () => markConnectionReady(character, generation, socket, 'puppetConfirmed', dispatch),
          () => {
            markConnectionReady(character, generation, socket, 'roomStateAccepted', dispatch);
            const actorId = account?.available_characters.find(
              (entry) => entry.name === character
            )?.id;
            if (account !== null && actorId !== undefined) {
              void markTargetMenusStale(queryClient, `account-${account.id}`, actorId);
            }
          },
          () => markEntryFailure(character, dispatch)
        );
      });
    },
    [account, dispatch, navigate]
  );

  /**
   * Resume a session after the browser wakes from a suspended page.
   *
   * A suspended WebSocket can remain in OPEN state while its transport is
   * already unusable. Remove it before starting the replacement so the stale
   * close event cannot reset the fresh session. This is deliberately separate
   * from explicit disconnect: wake recovery is not player intent to leave.
   */
  const resume = useCallback(
    (character: MyRosterEntry['name']) => {
      cancelReconnectTimer(character);
      const socket = sockets[character];
      if (socket) {
        delete sockets[character];
        const generation = connectionGenerations[character] ?? 0;
        finishGenerationResyncs(character, generation, dispatch);
        dispatch(setSessionConnectionStatus({ character, status: false }));
        dispatch(setSessionLifecycle({ character, lifecycleState: 'reconnecting' }));
        try {
          // A browser accepts only 1000 or 3000-4999 in close(); anything else
          // throws InvalidAccessError, and the socket stays open with nothing
          // tracking it. This used 1001, so every resume leaked a live session
          // that the server's auto-ping then kept alive (#4026).
          // PAGE_RESUME_CLOSE_CODE is ours, from the 4000-4999 app range.
          connectionDiagnostics.localClose(character, generation, 'page_resume');
          // This close is deliberate, but the replacement below is intentional
          // recovery rather than a player leave. Mark it before close so its
          // late callback cannot consume the replacement session.
          localCloseIntent.add(socket);
          socket.close(PAGE_RESUME_CLOSE_CODE, 'page-resume');
        } catch (error) {
          // The replacement connection is still attempted below. A throw here
          // means the old socket is still open and nothing tracks it any more:
          // that is the #4026 leak, so say so rather than hide it.
          console.warn('[socket] wake resume could not close the replaced socket', error);
        }
      }
      connect(character, true).catch(swallowReconnectError);
    },
    [connect, dispatch]
  );

  const send = useCallback(
    (character: MyRosterEntry['name'], command: string, clientRequestId?: string) => {
      const socket = sockets[character];
      if (socket && socket.readyState === WebSocket.OPEN) {
        const message: OutgoingMessage = [
          WS_MESSAGE_TYPE.TEXT,
          [command],
          { client_request_id: clientRequestId ?? globalThis.crypto.randomUUID() },
        ];
        socket.send(JSON.stringify(message));
      }
    },
    []
  );

  /**
   * Send a staff Commands-mode line (#3857): the same text frame, flagged so
   * the server tags everything it says back for the console.
   */
  const sendConsole = useCallback(
    (character: MyRosterEntry['name'], command: string) => {
      const socket = sockets[character];
      if (socket && socket.readyState === WebSocket.OPEN) {
        // The console shows the line above what the server says back to it.
        dispatch(addConsoleLine({ character, content: command, sent: true }));
        const message: OutgoingMessage = [WS_MESSAGE_TYPE.TEXT, [command], { console: true }];
        socket.send(JSON.stringify(message));
      }
    },
    [dispatch]
  );

  /**
   * Invoke a registered backend action over the websocket.
   *
   * The action dispatcher resolves `action` against the registry, runs the
   * backing service, and emits an `ACTION_RESULT` message. Listeners that
   * care about the outcome should subscribe via `useActionResult`.
   *
   * Silently no-ops when the named character has no open socket — callers
   * should disable buttons until the session is connected, but a missing
   * socket should not blow up the page.
   */
  const executeAction = useCallback(
    (character: MyRosterEntry['name'], action: string, kwargs: Record<string, unknown> = {}) => {
      const socket = sockets[character];
      if (socket && socket.readyState === WebSocket.OPEN) {
        const message: OutgoingMessage = [WS_MESSAGE_TYPE.EXECUTE_ACTION, [], { action, kwargs }];
        socket.send(JSON.stringify(message));
      }
    },
    []
  );

  /** Request a viewer-bound room snapshot without closing this socket. */
  const requestRoomState = useCallback(
    (character: MyRosterEntry['name']): string | null => {
      const existing = Array.from(pendingResync.values()).find(
        (pending) => pending.character === character
      );
      if (existing) return existing.requestId;
      const socket = sockets[character];
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        dispatch(
          setRoomStateResyncStatus({
            character,
            status: 'failure',
            error: 'Room state refresh unavailable while disconnected.',
          })
        );
        return null;
      }
      const requestId = globalThis.crypto.randomUUID();
      const generation = connectionGenerations[character] ?? 0;
      const timer = setTimeout(() => {
        const pending = pendingResync.get(requestId);
        if (!pending || pending.generation !== generation) return;
        finishResync(
          pending,
          dispatch,
          pending.snapshotAccepted ? 'partial' : 'failure',
          pending.snapshotAccepted
            ? 'Room state refreshed, but confirmation was lost.'
            : 'Room state refresh failed. Retry.'
        );
        if (!pending.snapshotAccepted) {
          dispatch(setSessionLifecycle({ character, lifecycleState: 'entry-error' }));
        }
      }, 6000);
      pendingResync.set(requestId, {
        character,
        generation,
        requestId,
        timer,
        snapshotAccepted: false,
      });
      dispatch(setRoomStateResyncStatus({ character, status: 'pending' }));
      const message: OutgoingMessage = [
        WS_MESSAGE_TYPE.REQUEST_ROOM_STATE,
        [],
        { client_request_id: requestId },
      ];
      try {
        socket.send(JSON.stringify(message));
      } catch {
        const pending = pendingResync.get(requestId);
        if (pending)
          finishResync(pending, dispatch, 'failure', 'Room state refresh failed. Retry.');
        return null;
      }
      return requestId;
    },
    [dispatch]
  );

  /** Current connection generation for `character` (0 if never connected). */
  const currentGeneration = useCallback(
    (character: MyRosterEntry['name']) => connectionGenerations[character] ?? 0,
    []
  );

  return {
    connect,
    resume,
    disconnect,
    send,
    sendConsole,
    disconnectAll,
    executeAction,
    requestRoomState,
    currentGeneration,
  };
}

function legacyFeedNote(
  message: ReturnType<typeof parseGameMessage>,
  kwargs: Record<string, unknown> | undefined
) {
  const subject = typeof kwargs?.subject === 'string' ? kwargs.subject : undefined;
  // A page carries its correspondent (#4129): `from_persona_id` and `from_name`
  // on both the recipient's line and the sender's echo (`outgoing`), so the
  // rail can group the two under one row.
  const fromPersonaId = Number(kwargs?.from_persona_id);
  const fromName = typeof kwargs?.from_name === 'string' ? kwargs.from_name : '';
  const from =
    Number.isFinite(fromPersonaId) && fromPersonaId > 0 && fromName
      ? { personaId: fromPersonaId, name: fromName }
      : undefined;
  return {
    kind: classifyText(kwargs?.type, kwargs?.category),
    content: message.content,
    ...(subject ? { subject } : {}),
    ...(from ? { from } : {}),
    ...(from && kwargs?.outgoing === true ? { outgoing: true } : {}),
    timestamp: new Date().toISOString(),
  };
}
